/**
 * Auth error mapping. The backend answers in English ("Invalid credentials"); the app
 * maps every known `detail` and status code to an AuthErrorKey and shows the message in
 * the chosen language (`getAuthErrorTranslation`). Unknown errors get a generic message,
 * never the raw backend text.
 */

export const AUTH_ERROR_KEYS = [
  'invalidCredentials',
  'emailAlreadyRegistered',
  'passwordTooShort',
  'passwordTooLong',
  'invalidName',
  'invalidEmail',
  'resetLinkInvalid',
  'resetLinkUsed',
  'resetLinkExpired',
  'sessionExpired',
  'googleUnavailable',
  'googleFailed',
  'tooManyRequests',
  'networkError',
  'serverError',
  'unknown',
] as const;

export type AuthErrorKey = (typeof AUTH_ERROR_KEYS)[number];

/** Where the error happened; some details and statuses mean different things per flow. */
export type AuthErrorContext = 'login' | 'register' | 'google' | 'passwordReset' | 'passwordResetRequest';

/** Exact `detail` strings raised by `backend/app/auth` (router, services, password policy). */
const DETAIL_TO_KEY: Readonly<Record<string, AuthErrorKey>> = {
  'Invalid credentials': 'invalidCredentials',
  'Email already registered': 'emailAlreadyRegistered',
  'Password must be at least 8 characters': 'passwordTooShort',
  'Password is too long': 'passwordTooLong',
  'Name must be between 1 and 100 characters': 'invalidName',
  'Invalid or expired reset link': 'resetLinkInvalid',
  'This reset link has already been used': 'resetLinkUsed',
  'This reset link has expired': 'resetLinkExpired',
  'Google OAuth not configured': 'googleUnavailable',
  'Token has expired': 'sessionExpired',
  'Invalid token': 'sessionExpired',
  'Token has been revoked': 'sessionExpired',
};

interface ErrorShape {
  status?: number;
  detail?: unknown;
  /** False when the request never got a response (offline, DNS, CORS, timeout). */
  reachedServer: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Read status and detail from an API client rejection or a raw axios error. */
function readError(error: unknown): ErrorShape | null {
  if (!isRecord(error)) return null;
  // Raw axios error: { isAxiosError, response: { status, data: { detail } } }
  if (isRecord(error.response)) {
    const { status, data } = error.response;
    return {
      status: typeof status === 'number' ? status : undefined,
      detail: isRecord(data) ? data.detail : undefined,
      reachedServer: true,
    };
  }
  // API client interceptor rejection: { message, status, data: { detail } }
  if ('status' in error || 'data' in error) {
    const { status, data } = error;
    return {
      status: typeof status === 'number' ? status : undefined,
      detail: isRecord(data) ? data.detail : undefined,
      reachedServer: typeof status === 'number' || data !== undefined,
    };
  }
  return null;
}

/** Known key for one detail message; FastAPI validation messages carry a "Value error, " prefix. */
function keyForMessage(message: string, context: AuthErrorContext): AuthErrorKey | null {
  const text = message.replace(/^Value error,\s*/i, '').trim();
  if (text === 'User not found') return context === 'passwordReset' ? 'resetLinkInvalid' : 'sessionExpired';
  return DETAIL_TO_KEY[text] ?? null;
}

function keyForDetail(detail: unknown, context: AuthErrorContext): AuthErrorKey | null {
  if (typeof detail === 'string') return keyForMessage(detail, context);
  if (Array.isArray(detail)) {
    // 422: [{ loc, msg, type }, ...]
    for (const item of detail) {
      if (!isRecord(item)) continue;
      if (typeof item.msg === 'string') {
        const key = keyForMessage(item.msg, context);
        if (key) return key;
      }
      if (Array.isArray(item.loc) && item.loc.includes('email')) return 'invalidEmail';
    }
  }
  return null;
}

/** Map any auth request failure to the key of a translated message. */
export function authErrorKey(error: unknown, context: AuthErrorContext): AuthErrorKey {
  const shape = readError(error);
  if (!shape) return 'unknown';

  const known = keyForDetail(shape.detail, context);
  if (known) return known;

  const { status } = shape;
  if (!shape.reachedServer) return 'networkError';
  if (status === undefined) return 'unknown';
  if (status === 429) return 'tooManyRequests';
  if (status >= 500) return 'serverError';
  if (status === 401) return context === 'login' ? 'invalidCredentials' : 'sessionExpired';
  return 'unknown';
}

/**
 * `?error=` codes the Google callback redirects to /login with (backend/app/auth/router.py):
 * A5's failures, and SEC1's refusals (`google_<reason>`, backend/app/auth/exceptions), which show
 * the generic Google message.
 */
const GOOGLE_REDIRECT_ERRORS: Readonly<Record<string, AuthErrorKey>> = {
  google_auth_failed: 'googleFailed',
  google_unavailable: 'googleUnavailable',
  google_email_unverified: 'googleFailed',
  google_invalid_email: 'googleFailed',
  google_account_mismatch: 'googleFailed',
};

/** The error to show for the Login page's `?error=`, or null for none or an unknown code. */
export function googleRedirectErrorKey(code: string | null): AuthErrorKey | null {
  return code !== null && Object.hasOwn(GOOGLE_REDIRECT_ERRORS, code) ? GOOGLE_REDIRECT_ERRORS[code] : null;
}
